import { computed, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { concatMap, finalize, tap } from 'rxjs';

import { CustomResultOfPaginatedListOfNotificationsListDto } from '../api/models/custom-result-of-paginated-list-of-notifications-list-dto';
import { GetNotificationsListQuery } from '../api/models/get-notifications-list-query';
import { NotificationImportanceLevel } from '../api/models/notification-importance-level';
import { NotificationTypes } from '../api/models/notification-types';
import { NotificationsService } from '../api/services/notifications.service';
import { NotificationsListDto } from './../api/models/notifications-list-dto';
import { UserStateService } from './authentication/user-state.service';
import { AppConfigService } from './config.service';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root',
})
export class AppNotificationService {
  notificationService = inject(NotificationsService);
  userSession = inject(UserStateService);
  titleService = inject(Title);

  #filteredImportanceLevel = signal<NotificationImportanceLevel[]>([]);
  #filteredNotificationTypes = signal<NotificationTypes[]>([]);
  #searchText = signal<string>('');

  filteredImportanceLevel = computed(() => this.#filteredImportanceLevel());
  filteredNotificationTypes = computed(() => this.#filteredNotificationTypes());

  initialLoading = signal<boolean>(false);
  loading = signal<boolean>(false);
  animateNotification = signal<boolean>(false);
  notificationTimerValue = signal<number>(0);
  latestNotification = signal<NotificationsListDto | null>(null);

  allNotificationsCount = signal<number>(0);
  unreadNotificationsCount = signal<number>(0);
  unreadCountLoading = signal<boolean>(false);

  #allNotifications = signal<NotificationsListDto[]>([]);
  #unreadNotifications = signal<NotificationsListDto[]>([]);

  allNotifications = computed(() =>
    this.#allNotifications().filter(
      (res) =>
        this.#filteredImportanceLevel()?.includes(res.importanceLevel) &&
        this.#filteredNotificationTypes()?.includes(res.notificationTypes) &&
        res.title?.includes(this.#searchText()),
    ),
  );

  unreadNotifications = computed(() =>
    this.#unreadNotifications().filter(
      (res) =>
        this.#filteredImportanceLevel()?.includes(res.importanceLevel) &&
        this.#filteredNotificationTypes()?.includes(res.notificationTypes) &&
        res.title?.includes(this.#searchText()),
    ),
  );

  setFilteredImportanceLevel(values: NotificationImportanceLevel[]) {
    this.#filteredImportanceLevel.set(Object.assign([], values));
  }

  setFilteredNotificationTypes(values: NotificationTypes[]) {
    this.#filteredNotificationTypes.set(Object.assign([], values));
  }

  onChangeSearchText(text: string) {
    this.#searchText.set(text);
  }

  handleNewNotification(notification: NotificationsListDto) {
    this.#unreadNotifications.update((values) => [notification, ...values]);
    this.#allNotifications.update((values) => [notification, ...values]);
    this.unreadNotificationsCount.update((count) => count + 1);
    this.allNotificationsCount.update((count) => count + 1);

    this.latestNotification.set(notification);

    this.animateNotification.set(true);
    const timeout = setTimeout(() => {
      this.animateNotification.set(false);
      clearTimeout(timeout);
    }, 400);

    if (AppConfigService.config.isShowNotificationPopup)
      UtilService.onPushNotification(
        this.titleService.getTitle(),
        notification.title,
      );
  }

  // ==============================
  // ✅ FIXED INFINITE SCROLL LOGIC
  // ==============================
  getNotificationsApi(isInfiniteScroll = true, pageSize = 30, pageNumber = 1) {
    const body: GetNotificationsListQuery = {
      pageSize,
      pageNumber: isInfiniteScroll ? pageNumber : 1,
    };

    this.userSession.isFetchingNotifications = true;
    this.loading.set(true);
    if (this.#unreadNotifications().length === 0)
      this.unreadCountLoading.set(true);

    return this.notificationService
      .notificationsGetNotificationsList({
        body: { ...body, justNotSeenNotifications: false },
      })
      .pipe(
        concatMap(
          (result: CustomResultOfPaginatedListOfNotificationsListDto) => {
            const items = result.data?.items ?? [];
            const totalCount = result.data?.totalCount ?? 0;

            // ✅ APPEND OR REPLACE (ALL)
            if (isInfiniteScroll && pageNumber > 1) {
              this.#allNotifications.update((prev) => [...prev, ...items]);
            } else {
              this.#allNotifications.set(items);
            }

            this.allNotificationsCount.set(totalCount);

            return this.notificationService
              .notificationsGetNotificationsList({
                body: { ...body, justNotSeenNotifications: true },
              })
              .pipe(
                tap(
                  (
                    result: CustomResultOfPaginatedListOfNotificationsListDto,
                  ) => {
                    const unreadItems = result.data?.items ?? [];
                    const unreadTotal = result.data?.totalCount ?? 0;

                    // ✅ APPEND OR REPLACE (UNREAD)
                    if (isInfiniteScroll && pageNumber > 1) {
                      this.#unreadNotifications.update((prev) => [
                        ...prev,
                        ...unreadItems,
                      ]);
                    } else {
                      this.#unreadNotifications.set(unreadItems);
                    }

                    this.unreadNotificationsCount.set(unreadTotal);
                  },
                ),
              );
          },
        ),
        finalize(() => {
          this.loading.set(false);
          this.unreadCountLoading.set(false);
          this.userSession.isFetchingNotifications = false;
        }),
      );
  }
}
